/**
 * useChatHydration — DB 메시지를 useAgentChat state에 주입
 *
 * 설계 원칙 (ARCHITECTURE.md §state layer):
 * - ChatZone에서 **명시적으로 호출**되는 hook (ChatZone은 hydration 과정을 관찰 가능)
 * - useAgentChat의 내부 useEffect를 제거하고 이 훅에서 관장
 * - 상태를 외부에 노출 → 로딩 중/완료/에러 UI 표시 가능
 *
 * 동작:
 * 1. sessionId가 있으면 useAgentMessages로 DB에서 메시지 로드
 * 2. 로드 완료 시 useAgentChat.hydrate()를 호출해 state에 주입
 * 3. 동일 sessionId는 한 번만 hydrate (invalidate 후 재로드 시에도 덮어쓰지 않음)
 * 4. sessionId가 바뀌면 새 세션으로 re-hydrate
 * 5. 스트리밍 중이면 hydrate 스킵
 */

import { useEffect, useRef } from "react";
import { useAgentMessages } from "../data/sessions";
import type { ChatMessage } from "@modootoday/core-agent/types";

export interface UseChatHydrationOptions {
  /** 로드할 세션 ID (null 이면 no-op) */
  sessionId: string | null | undefined;
  /** useAgentChat이 반환한 hydrate 함수 */
  hydrate: (messages: ChatMessage[], sessionId: string) => void;
  /** useAgentChat.isStreaming — 스트리밍 중 hydrate 스킵 */
  isStreaming: boolean;
}

export interface UseChatHydrationReturn {
  /** DB에서 fetch 중 */
  isLoading: boolean;
  /** hydration 완료 (해당 세션의 메시지가 state에 주입됨) */
  isHydrated: boolean;
  /** 로드 에러 */
  error: Error | null;
  /** 로드된 메시지 수 (디버깅) */
  loadedCount: number;
}

export function useChatHydration(
  opts: UseChatHydrationOptions,
): UseChatHydrationReturn {
  const { sessionId, hydrate, isStreaming } = opts;

  const {
    data: loadedMessages,
    isLoading,
    isSuccess,
    error,
  } = useAgentMessages(sessionId ?? null);

  // 마지막으로 hydrate 성공한 sessionId 추적 (중복 방지)
  const hydratedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      // 세션이 없으면 ref 초기화 (새 대화 모드 진입)
      hydratedSessionIdRef.current = null;
      return;
    }
    if (isStreaming) return; // 스트리밍 중 스킵
    if (!isSuccess) return; // 쿼리 미완료
    if (hydratedSessionIdRef.current === sessionId) return; // 이미 hydrate됨

    const fromDb = loadedMessages ?? [];
    hydrate(fromDb, sessionId);
    hydratedSessionIdRef.current = sessionId;
  }, [sessionId, isSuccess, loadedMessages, isStreaming, hydrate]);

  return {
    isLoading,
    isHydrated: hydratedSessionIdRef.current === sessionId && !!sessionId,
    error: (error as Error | null) ?? null,
    loadedCount: loadedMessages?.length ?? 0,
  };
}
